/**
 * The article, as a document.
 *
 * The people writing here are clinic staff. They should see a finished page -
 * headings that look like headings, a table that looks like a table, a coloured
 * box that looks like a box - and never a hash, a pipe or an angle bracket.
 * Markup belongs to the export, which is where a format is chosen deliberately.
 *
 * WHAT THIS IS NOT: a Markdown editor with a preview. A split view still asks
 * somebody to understand two representations of one article and hold both in
 * their head. Here there is one, and it is the finished one.
 *
 * Markdown remains the stored truth (see rich-text.ts), because four exports,
 * the legal check, the editorial checks and the section splitter all read it.
 * The conversion happens at this boundary and nowhere else.
 *
 * EVERY CONTROL ROUND-TRIPS. What GFM cannot carry - a colour, a size, a box -
 * is written as inline HTML and passed back through untouched, so nothing here
 * can be lost by saving and reopening. A control whose output did not survive
 * that would be a way to lose work, which is worse than not having the control.
 */
import { currentBrand } from '../theme-apply.ts';
import { promptDialog } from './AppDialog.tsx';
import { ColorPicker } from './ColorPicker.tsx';
import { THEME_PRESETS } from '../theme.ts';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { Color, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { MARKDOWN_DRAG_MIME, toHtml, toMarkdown } from '../rich-text.ts';
import { BlockClass, Callout, DocBlock } from './Callout.ts';
import { Select } from './Select.tsx';
import { AnimatePresence, m } from './motion.tsx';

/**
 * The four boxes, by what they are for rather than by colour.
 *
 * A clinic reaches for these constantly - 「注意」 for a caveat, 「ポイント」 for
 * the thing to remember - and Markdown has no equivalent, so each is stored as a
 * div with a class and passed through the conversion untouched.
 */
const BOXES = [
  { kind: 'info', label: 'ℹ', title: '補足ボックス（青）' },
  { kind: 'warn', label: '⚠', title: '注意ボックス（黄）' },
  { kind: 'note', label: '✓', title: 'ポイントボックス（緑）' },
  { kind: 'stop', label: '⃠', title: '禁止・NGボックス（赤）' },
] as const;

/**
 * A custom box starts from the clinic's own colour rather than from black -
 * it is written into the article as a hex, so it is the colour in force when
 * the box is made, not a token that would change with a later theme.
 */
const defaultBoxColor = () => currentBrand();

/** Text sizes, as words. A writer picks 「大」, not 「1.25em」. */
const SIZES = [
  { label: '小', value: '0.875em' },
  { label: '標準', value: '' },
  { label: '大', value: '1.15em' },
  { label: '特大', value: '1.4em' },
] as const;

function Tool({
  label,
  title,
  active,
  disabled,
  wide,
  onClick,
}: {
  label: string;
  title: string;
  active?: boolean;
  disabled?: boolean;
  /** A word rather than a glyph: it may break the square grid, and says so. */
  wide?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`docbar__tool${active ? ' docbar__tool--on' : ''}${wide ? ' docbar__tool--wide' : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={active ?? false}
      disabled={disabled ?? false}
      // The editor keeps focus, so a click acts on the selection that was there
      // rather than on nothing.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/**
 * The block styles, as a menu rather than four buttons.
 *
 * Google Docs puts these in one dropdown for a reason worth copying: they are
 * mutually exclusive - a paragraph is not also a heading - and four aria-pressed
 * buttons say the opposite. It also reclaims the width of three buttons in a bar
 * that has to fit on a laptop.
 */
const BLOCK_STYLES = [
  { value: 'p', label: '標準テキスト' },
  { value: 'h1', label: '見出し 1' },
  { value: 'h2', label: '見出し 2' },
  { value: 'h3', label: '見出し 3' },
] as const;

/**
 * Working on a table, when the caret is in one.
 *
 * Its own strip rather than five more buttons in the format row, and shown only
 * inside a table - which is what Google Docs does, and what makes a table feel
 * like something you can work on rather than something you inserted once. Every
 * action names the direction it works in: a bare 「行を追加」 is a button somebody
 * presses to find out what it does.
 *
 * Column widths are deliberately NOT draggable. The document is stored as
 * Markdown (see rich-text.ts) and a GFM table carries no widths, so a dragged
 * column would be lost on the next save - and a control that silently discards
 * work is worse than an absent one.
 */
function TableBar({ editor, canMerge }: { editor: Editor; canMerge: boolean }) {
  return (
    <div className="docbar__row docbar__row--table" role="group" aria-label="表の編集">
      <span className="docbar__label">表</span>
      <Tool wide label="行を上に" title="上に行を追加" onClick={() => editor.chain().focus().addRowBefore().run()} />
      <Tool wide label="行を下に" title="下に行を追加" onClick={() => editor.chain().focus().addRowAfter().run()} />
      <Tool wide label="列を左に" title="左に列を追加" onClick={() => editor.chain().focus().addColumnBefore().run()} />
      <Tool wide label="列を右に" title="右に列を追加" onClick={() => editor.chain().focus().addColumnAfter().run()} />
      <span className="docbar__sep" aria-hidden />
      <Tool wide label="行を削除" title="この行を削除" onClick={() => editor.chain().focus().deleteRow().run()} />
      <Tool wide label="列を削除" title="この列を削除" onClick={() => editor.chain().focus().deleteColumn().run()} />
      <span className="docbar__sep" aria-hidden />
      {/* Merging is what a real comparison table needs, and what nobody could do
          here: the cell that spans two columns had to be faked with an empty one. */}
      <Tool
        wide
        label="セルを結合"
        title="選択したセルを結合／分割"
        disabled={!canMerge}
        onClick={() => editor.chain().focus().mergeOrSplit().run()}
      />
      <Tool wide label="見出し行" title="1行目を見出しにする／戻す" onClick={() => editor.chain().focus().toggleHeaderRow().run()} />
      <span className="docbar__spacer" />
      <Tool wide label="表を削除" title="表ごと削除" onClick={() => editor.chain().focus().deleteTable().run()} />
    </div>
  );
}

/**
 * The colour of one CTA or supervisor card, when the caret is in one.
 *
 * The TableBar pattern again: a strip that appears only where it applies, so
 * the control sits next to the thing it changes instead of in a settings page
 * nobody connects with this one button. Presets first (one click, all already
 * readable), the full picker behind 「その他の色」, and 「テーマの色」 to hand the
 * block back to the clinic's theme - which is the default, and stays the
 * default for every block nobody touched.
 *
 * Only the chosen hex is stored; the button text, title and tint are derived
 * from it with a contrast check (shared/theme.ts blockColors), so no pick can
 * produce white text on a pale button.
 */
function BlockBar({ editor, variant, color }: { editor: Editor; variant: string; color: string | null }) {
  const [custom, setCustom] = useState(false);
  const shown = color ?? currentBrand();
  const set = (next: string | null) => editor.chain().updateAttributes('docBlock', { color: next }).run();
  return (
    <div className="docbar__row docbar__row--block" role="group" aria-label="ブロックの色">
      <span className="docbar__label">{variant === 'ctabox' ? 'CTAの色' : '監修者カードの色'}</span>
      <Tool wide label="テーマの色" title="クリニックのテーマ色に戻す" active={color === null} onClick={() => set(null)} />
      <span className="docbar__sep" aria-hidden />
      {THEME_PRESETS.map((preset) => (
        <button
          key={preset.hex}
          type="button"
          className="themepick__swatch docbar__swatch"
          style={{ background: preset.hex }}
          title={preset.label}
          aria-label={preset.label}
          aria-pressed={color === preset.hex}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => set(preset.hex)}
        />
      ))}
      <Tool
        wide
        label="その他の色"
        title="色を自由に選ぶ"
        active={custom}
        onClick={() => setCustom((open) => !open)}
      />
      {custom && (
        <div className="docbar__picker">
          <ColorPicker idPrefix="block-color" value={shown} onChange={set} />
        </div>
      )}
    </div>
  );
}

/**
 * A toolbar row that comes and goes - opening, not appearing.
 *
 * The second row appears when an editor is clicked into, the 表 and ブロックの色
 * strips when the caret enters a table or a CTA. Each one used to push the
 * whole document down 30-40px on one frame, and pull it back up the moment the
 * caret left - the jolt under every insert, because an inserted CTA puts the
 * caret in the CTA. Its height is animated instead, so the document slides.
 */
function ToolbarRow({ show, children }: { show: boolean; children: ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {show && (
        <m.div
          style={{ overflow: 'hidden' }}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          {children}
        </m.div>
      )}
    </AnimatePresence>
  );
}

/**
 * What the toolbar has to redraw itself for.
 *
 * `useEditor` does NOT re-render on every transaction - v3 made that opt-in, and
 * without this hook the whole toolbar was frozen at its first render: B never lit
 * up inside bold text, the style menu never followed the caret out of a heading,
 * undo stayed disabled forever, and the table strip never appeared because
 * `isActive('table')` was still answering for an empty document.
 *
 * Subscribed to as a snapshot of exactly these values rather than by re-rendering
 * on every keystroke: the write screen stacks one editor per section, and a
 * document-wide re-render per character across eight of them is the cost this
 * default exists to avoid. The equality check keeps a plain typing transaction
 * from touching React at all - the flags only change when the caret crosses a
 * boundary.
 */
function useToolbarState(editor: Editor) {
  return useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      bold: instance.isActive('bold'),
      italic: instance.isActive('italic'),
      strike: instance.isActive('strike'),
      highlight: instance.isActive('highlight'),
      bulletList: instance.isActive('bulletList'),
      orderedList: instance.isActive('orderedList'),
      taskList: instance.isActive('taskList'),
      blockquote: instance.isActive('blockquote'),
      codeBlock: instance.isActive('codeBlock'),
      link: instance.isActive('link'),
      table: instance.isActive('table'),
      callout: instance.isActive('callout'),
      calloutKind: (instance.getAttributes('callout').kind as string | undefined) ?? null,
      calloutColor: (instance.getAttributes('callout').color as string | null | undefined) ?? null,
      docBlock: instance.isActive('docBlock'),
      blockVariant: (instance.getAttributes('docBlock').variant as string | undefined) ?? 'bio',
      blockColor: (instance.getAttributes('docBlock').color as string | null | undefined) ?? null,
      canUndo: instance.can().undo(),
      canRedo: instance.can().redo(),
      canMerge: instance.can().mergeOrSplit(),
      heading: ([1, 2, 3] as const).find((level) => instance.isActive('heading', { level })),
      fontSize: (instance.getAttributes('textStyle').fontSize as string | undefined) ?? '',
      color: (instance.getAttributes('textStyle').color as string | undefined) ?? '#2b2130',
    }),
  });
}

function Toolbar({ editor, expanded }: { editor: Editor; expanded: boolean }) {
  const state = useToolbarState(editor);
  const addLink = async () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const href = await promptDialog({
      title: 'リンクを設定',
      message: 'リンク先のURLを入力してください。空欄にするとリンクを外します。',
      defaultValue: previous ?? 'https://',
      confirmLabel: '設定する',
    });
    if (href === null) return;
    if (href.trim() === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
  };

  const addImage = async () => {
    const src = await promptDialog({
      title: '画像を挿入',
      message: '画像のURLを入力してください。',
      defaultValue: 'https://',
      confirmLabel: '挿入する',
    });
    if (src === null || src.trim() === '') return;
    editor.chain().focus().setImage({ src: src.trim() }).run();
  };


  return (
    <div className="docbar" role="toolbar" aria-label="書式">
      {/* Row one: what somebody reaches for while writing a sentence. */}
      <div className="docbar__row">
        {/* Undo and redo at the left end, where every document editor keeps them. */}
        <Tool label="↶" title="元に戻す" disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()} />
        <Tool label="↷" title="やり直す" disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()} />
        <span className="docbar__sep" aria-hidden />
        {/*
          The article's title is its own field, so a 見出し1 here becomes a second
          title in every export. Offered because writers expect it, and because
          refusing it silently is worse than letting them choose.
        */}
        {/*
          The application's own select, not the platform's.

          A native <select> renders its list in the operating system's layer,
          where no stylesheet reaches: the one control in this toolbar that
          opened a menu opened a grey Windows one in the middle of a sakura
          document. Select.tsx exists for exactly that and is already used by
          every other list in the app - see the note at the top of that file.
        */}
        <Select
          className="docbar__style"
          ariaLabel="段落スタイル"
          value={state.heading ? `h${state.heading}` : 'p'}
          options={BLOCK_STYLES.map((style) => ({ value: style.value, label: style.label }))}
          onChange={(value) => {
            const chain = editor.chain().focus();
            if (value === 'p') chain.setParagraph().run();
            else chain.setHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 }).run();
          }}
        />
        <span className="docbar__sep" aria-hidden />
        <Tool label="B" title="太字" active={state.bold} onClick={() => editor.chain().focus().toggleBold().run()} />
        <Tool label="I" title="斜体" active={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <Tool label="S" title="打ち消し線" active={state.strike} onClick={() => editor.chain().focus().toggleStrike().run()} />
        <Tool label="🖍" title="マーカー" active={state.highlight} onClick={() => editor.chain().focus().toggleHighlight().run()} />
        {/* The native picker: familiar, keyboard-reachable, no menu of our own. */}
        <label className="docbar__color" title="文字色">
          <span aria-hidden>A</span>
          <input
            type="color"
            aria-label="文字色"
            value={state.color}
            onChange={(event) => editor.chain().focus().setColor(event.target.value).run()}
          />
        </label>
        <span className="docbar__sep" aria-hidden />
        <Tool label="•" title="箇条書き" active={state.bulletList} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <Tool label="1." title="番号つきリスト" active={state.orderedList} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <Tool label="☑" title="チェックリスト" active={state.taskList} onClick={() => editor.chain().focus().toggleTaskList().run()} />
        <span className="docbar__sep" aria-hidden />
        <Tool label="🔗" title="リンク" active={state.link} onClick={() => void addLink()} />
        <Tool label="🖼" title="画像を挿入" onClick={() => void addImage()} />
        <Tool
          label="⊞"
          title="表を挿入（3列2行）"
          active={state.table}
          onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 3, withHeaderRow: true }).run()}
        />
      </div>

      {/*
        Row two, only while this editor is in use.

        The write screen stacks one editor per section: eight sections meant
        eight permanent two-row toolbars, and the page was more chrome than
        article. What is on this row - size, boxes, rules - is reached
        deliberately rather than mid-sentence, so it can wait for the caret.
      */}
      <ToolbarRow show={expanded}>
        <div className="docbar__row docbar__row--second">
          <span className="docbar__label">サイズ</span>
          {SIZES.map((size) => (
            <Tool
              key={size.label}
              label={size.label}
              title={`文字サイズ：${size.label}`}
              active={state.fontSize === size.value}
              onClick={() =>
                size.value === ''
                  ? editor.chain().focus().unsetFontSize().run()
                  : editor.chain().focus().setFontSize(size.value).run()
              }
            />
          ))}
          <Tool wide label="色なし" title="文字色を戻す" onClick={() => editor.chain().focus().unsetColor().run()} />
          <span className="docbar__sep" aria-hidden />
          <Tool label="❝" title="引用" active={state.blockquote} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
          <Tool label="&lt;/&gt;" title="コード" active={state.codeBlock} onClick={() => editor.chain().focus().toggleCodeBlock().run()} />
          <Tool label="—" title="区切り線" onClick={() => editor.chain().focus().setHorizontalRule().run()} />
          <span className="docbar__sep" aria-hidden />
          <span className="docbar__label">BOX</span>
          {/*
            Pressed on a paragraph, these WRAP it. The old behaviour inserted a
            new box full of placeholder text below the cursor, so turning a
            sentence into a warning meant retyping it - and the box was dropped
            by the schema on the way in anyway. Pressing the box you are already
            in takes it off, which is why they carry a pressed state.
          */}
          {BOXES.map((box) => (
            <button
              key={box.kind}
              type="button"
              className={`docbar__box docbar__box--${box.kind}${
                state.callout && state.calloutKind === box.kind && !state.calloutColor
                  ? ' docbar__box--on'
                  : ''
              }`}
              title={`${box.title}（選択した段落を囲みます）`}
              aria-label={box.title}
              aria-pressed={state.callout && state.calloutKind === box.kind && !state.calloutColor}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => editor.commands.toggleCallout({ kind: box.kind })}
            >
              {box.label}
            </button>
          ))}
          {/*
            Any other colour. The swatch shows the box's own colour when the
            caret is in a custom one, so changing it is a matter of opening the
            picker rather than of guessing what it currently is.
          */}
          <label
            className={`docbar__box docbar__box--pick${state.calloutColor ? ' docbar__box--on' : ''}`}
            title="好きな色のボックス（選択した段落を囲みます）"
            // A CSS custom property: React types style as known properties only.
            style={state.calloutColor ? ({ '--callout': state.calloutColor } as CSSProperties) : undefined}
          >
            <span aria-hidden>＋</span>
            <input
              type="color"
              aria-label="ボックスの色を選ぶ"
              value={state.calloutColor ?? defaultBoxColor()}
              onChange={(event) =>
                editor.commands.toggleCallout({ kind: 'custom', color: event.target.value })
              }
            />
          </label>
          {state.callout && (
            <Tool
              wide
              label="ボックス解除"
              title="ボックスを外して本文に戻す"
              onClick={() => editor.commands.unsetCallout()}
            />
          )}
        </div>
      </ToolbarRow>

      {/* Only inside a table. A row of permanently greyed buttons teaches nobody. */}
      <ToolbarRow show={state.table}>
        <TableBar editor={editor} canMerge={state.canMerge} />
      </ToolbarRow>
      <ToolbarRow show={state.docBlock}>
        <BlockBar editor={editor} variant={state.blockVariant} color={state.blockColor} />
      </ToolbarRow>
    </div>
  );
}

/** A document change this soon after a key is the writer typing, not an insert. */
const TYPING_MS = 400;

/**
 * Blocks arrive and make room smoothly, the way list items do.
 *
 * Inserting a CTA, a box, a table, a picture, or a section the AI just wrote
 * or rewrote changed the document in one frame: the new block was simply
 * there, and everything under it teleported down by its height. The screen's
 * own glide (motion.tsx, useBlockGlide) stops at the editor on purpose - it is
 * for cards and lists, not for text - so the document needs its own.
 *
 * Top-level blocks only, which is the grain an insert happens at: a block that
 * is new fades in, a block that was pushed starts at its old place and slides
 * to its new one (FLIP, on offsetTop so scrolling is never mistaken for a
 * move). It runs on the editor's own `transaction` event, which fires after
 * ProseMirror has updated the DOM and before the browser paints, and on a
 * ResizeObserver for what moves later - a picture finishing loading.
 *
 * TYPING IS LEFT ALONE. A new line under the caret that slid into place would
 * be a document that lags behind the writer, so any change within TYPING_MS of
 * a key or an IME composition is recorded and not animated.
 */
function useDocGlide(editor: Editor | null) {
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    const tops = new WeakMap<Element, number>();
    const texts = new WeakMap<Element, string>();
    let typedAt = 0;
    const blocks = () => Array.from(dom.children) as HTMLElement[];

    const glide = (arriving: boolean) => {
      const quiet = still.matches || performance.now() - typedAt < TYPING_MS;
      for (const el of blocks()) {
        const before = tops.get(el);
        // From the top of the document, not of whatever is positioned above
        // it: the toolbar opening a row is not the text moving.
        const top = el.offsetTop - (el.offsetParent === dom ? 0 : dom.offsetTop);
        const said = texts.get(el);
        const text = el.textContent ?? '';
        tops.set(el, top);
        texts.set(el, text);
        if (quiet) continue;
        if (before === undefined) {
          if (arriving) el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
          continue;
        }
        /*
         * The same block with different words - ProseMirror keeps the element
         * and swaps its text, which is what a rewrite or an AI fix does to a
         * paragraph. A soft fade, so the writer sees which lines changed rather
         * than the text silently being different.
         */
        if (arriving && said !== undefined && said !== text) {
          el.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 320, easing: 'ease-out' });
        }
        if (Math.abs(before - top) >= 2) {
          el.animate([{ transform: `translateY(${before - top}px)` }, { transform: 'none' }], {
            duration: 260,
            easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
          });
        }
      }
    };

    const typed = () => {
      typedAt = performance.now();
    };
    const onTransaction = ({ transaction }: { transaction: { docChanged: boolean } }) =>
      glide(transaction.docChanged);
    /*
     * CAPTURE, so it runs before ProseMirror's own keydown handler: Enter is
     * handled there, synchronously, and the transaction it dispatches reached
     * the glide before a bubbling listener had marked the key - measured, a new
     * line slid the text under it. Any key anywhere counts; the cost of
     * skipping an animation within TYPING_MS of typing elsewhere is nothing.
     */
    window.addEventListener('keydown', typed, true);
    window.addEventListener('compositionupdate', typed, true);
    editor.on('transaction', onTransaction);
    const resized = new ResizeObserver(() => glide(false));
    resized.observe(dom);
    glide(false);
    // An editor that mounts with text in it is a section that was just written:
    // it arrives like a block does, rather than appearing in one frame.
    if (!still.matches) dom.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });

    return () => {
      window.removeEventListener('keydown', typed, true);
      window.removeEventListener('compositionupdate', typed, true);
      editor.off('transaction', onTransaction);
      resized.disconnect();
    };
  }, [editor]);
}

export function RichEditor({
  value,
  onChange,
  onSelectionChange,
  editorRef,
  /** A section inside the write screen is smaller than a whole article. */
  compact = false,
}: {
  /** The article, as Markdown. */
  value: string;
  /** Called with Markdown, so the screen above never handles HTML. */
  onChange: (markdown: string) => void;
  /**
   * The selected text and where it is on screen, for tools that act on a
   * passage. The rect is what lets a control sit AT the passage instead of in a
   * panel somewhere else that has to quote it back.
   */
  onSelectionChange?: (selection: string, rect: DOMRect | null) => void;
  /** Exposed so the outline rail and the checks can scroll to a passage. */
  editorRef?: { current: Editor | null };
  compact?: boolean;
}) {
  /*
   * What this component last emitted.
   *
   * Every keystroke converts the document to Markdown and hands it up; that
   * value comes back down as `value`. Without remembering what we sent, the
   * effect below would treat our own output as an outside change and reset the
   * document mid-sentence - caret to the top, on every character.
   */
  const emitted = useRef(value);

  /*
   * Whether this editor is the one being used.
   *
   * Tracked on the WRAPPER rather than on the editor's own focus, because the
   * toolbar is part of the editor as far as a writer is concerned: pressing a
   * colour swatch moves the browser's focus out of the document and into an
   * input, and a toolbar that collapsed at that moment would be a toolbar that
   * disappears when you use it.
   */
  const [active, setActive] = useState(false);

  /*
   * The editor, reachable from `editorProps`.
   *
   * Those handlers are built before `useEditor` returns, so they cannot close
   * over the editor itself; a ref filled in immediately below is how a drop
   * handler gets to run a command.
   */
  const editorHandle = useRef<Editor | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ inline: false }),
      Highlight,
      TextStyle,
      Color,
      FontSize,
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Callout,
      DocBlock,
      BlockClass,
      Placeholder.configure({ placeholder: '本文をここに書きます。' }),
    ],
    content: toHtml(value),
    editorProps: {
      /*
       * A tile dropped into the document becomes what it depicts.
       *
       * The image strip and the 差し込み tiles have been draggable since they
       * existed, and both say so on screen; what arrived was the literal text
       * `![カバー画像](/api/v1/images/…)`, because nothing here handled the drop
       * and ProseMirror's default for a text/plain drop is to insert text.
       *
       * Only our own MIME type is intercepted. A drag from another window is
       * text and stays text, and an internal drag - `moved`, a passage the
       * writer is moving within the document - is left to ProseMirror, which
       * already does it correctly.
       */
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const markdown = event.dataTransfer?.getData(MARKDOWN_DRAG_MIME);
        if (!markdown) return false;
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
        if (!at) return false;
        event.preventDefault();
        // Where it was DROPPED, not where the caret happened to be: the whole
        // point of dragging is choosing the place.
        editorHandle.current?.chain().focus().insertContentAt(at.pos, toHtml(markdown)).run();
        return true;
      },
      attributes: {
        class: compact ? 'doc doc--compact' : 'doc',
        // A document, not a form field: the label belongs to the surrounding
        // field, and the role tells a screen reader this is rich text.
        role: 'textbox',
        'aria-multiline': 'true',
      },
    },
    onUpdate: ({ editor: instance }) => {
      const markdown = toMarkdown(instance.getHTML());
      emitted.current = markdown;
      onChange(markdown);
    },
    onSelectionUpdate: ({ editor: instance }) => {
      if (!onSelectionChange) return;
      const { from, to } = instance.state.selection;
      if (from === to) {
        onSelectionChange('', null);
        return;
      }
      /*
       * Where the passage IS, so a control can be put beside it.
       *
       * Taken from the view rather than from `window.getSelection()`: the
       * browser's selection is gone the moment focus moves into the panel this
       * rect positions, and ProseMirror's own coordinates are still correct
       * then.
       */
      const start = instance.view.coordsAtPos(from);
      const end = instance.view.coordsAtPos(to);
      /*
       * PAGE coordinates, not viewport ones.
       *
       * `coordsAtPos` answers in viewport space, which is what every other
       * popup in this app wants because they are `position: fixed` and are
       * re-measured on every scroll. This one anchors to a passage inside a
       * long document the writer scrolls constantly, and a panel that has to
       * chase its anchor on every scroll event is a panel that lags behind it.
       * Adding the scroll offset once, here, makes the rect describe a place in
       * the DOCUMENT - so an absolutely-positioned panel stays glued to the
       * sentence with no listeners at all.
       */
      const left = Math.min(start.left, end.left) + window.scrollX;
      const top = Math.min(start.top, end.top) + window.scrollY;
      onSelectionChange(
        instance.state.doc.textBetween(from, to, '\n'),
        new DOMRect(
          left,
          top,
          Math.max(end.right, start.right) + window.scrollX - left,
          Math.max(end.bottom, start.bottom) + window.scrollY - top,
        ),
      );
    },
  });

  useEffect(() => {
    editorHandle.current = editor;
    if (editorRef) editorRef.current = editor;
  }, [editor, editorRef]);

  useDocGlide(editor);

  /*
   * An outside change - a redrawn section, a recovered draft, a generated body -
   * replaces the document. Our own emissions are skipped by the comparison
   * above, so this never fires for typing.
   */
  useEffect(() => {
    if (!editor || value === emitted.current) return;
    emitted.current = value;
    editor.commands.setContent(toHtml(value), { emitUpdate: false });
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div
      className={`docwrap${compact ? ' docwrap--compact' : ''}${active ? ' docwrap--active' : ''}`}
      // React's onFocus/onBlur are focusin/focusout: they fire for the toolbar
      // and the document alike, which is what makes the wrapper the right owner.
      onFocus={() => setActive(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setActive(false);
      }}
    >
      {/* Always in a compact editor, and on the full one too: an article editor
          that is the whole screen has nothing to compete with. */}
      <Toolbar editor={editor} expanded={active || !compact} />
      <EditorContent editor={editor} />
    </div>
  );
}
